import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Alert, Button, Checkbox, Empty, Input, Modal, Skeleton } from 'antd';
import { FolderOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

import { fileIcon, formatSize } from '../editor/misc-nodes';
import type { PickedDiskFile } from '../lib/api/kb';
import { aggregateFileCounts, folderPath, searchDiskFiles } from '../lib/diskFolders';
import { diskApi, type DiskFileDto } from '../lib/wikiApi';

/**
 * Окно «Файл с Диска» для редактора статьи (slash-меню → «Файл с Диска»).
 *
 * Поиск по имени среди всех папок + обзор папок, как на странице «Диск». Можно выбрать
 * несколько файлов, в том числе из разных папок. Списки берутся теми же запросами и из
 * того же кэша, что и сама страница «Диск» (ключи ['wiki','disk',…]).
 */
export interface DiskFilePickerModalProps {
  open: boolean;
  /** Закрыли окно без выбора. */
  onCancel: () => void;
  /** «Вставить»: выбранные файлы в порядке выбора. */
  onPick: (files: PickedDiskFile[]) => void;
}

export function DiskFilePickerModal({ open, onCancel, onPick }: DiskFilePickerModalProps): JSX.Element {
  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title="Файл с Диска"
      footer={null}
      width={640}
      destroyOnHidden
    >
      {/* Тело живёт только пока окно открыто — выбор и поиск каждый раз начинаются с чистого листа. */}
      {open && <PickerBody onCancel={onCancel} onPick={onPick} />}
    </Modal>
  );
}

function PickerBody({ onCancel, onPick }: { onCancel: () => void; onPick: (files: PickedDiskFile[]) => void }): JSX.Element {
  const [query, setQuery] = useState('');
  const [folderId, setFolderId] = useState<string | null>(null);
  // Выбранные файлы (id → файл) — сохраняются при переходе между папками и поиском.
  const [checked, setChecked] = useState<Map<string, PickedDiskFile>>(() => new Map());

  const foldersQuery = useQuery({
    queryKey: ['wiki', 'disk', 'folders'],
    queryFn: () => diskApi.folders(),
    retry: false,
    staleTime: 30_000,
  });
  const folders = useMemo(() => foldersQuery.data ?? [], [foldersQuery.data]);
  const totals = useMemo(() => aggregateFileCounts(folders), [folders]);

  const searching = query.trim().length >= 2;

  // Файлы открытой папки.
  const filesQuery = useQuery({
    queryKey: ['wiki', 'disk', 'files', folderId],
    queryFn: () => diskApi.files(folderId!),
    enabled: !!folderId && !searching,
    retry: false,
    staleTime: 30_000,
  });

  // Поиск: готового «искать по всему Диску» на сервере нет — читаем файлы всех непустых папок
  // (те же запросы, что на странице «Диск», кэшируются) и фильтруем по имени здесь.
  const searchFolders = useMemo(() => folders.filter((f) => f.fileCount > 0), [folders]);
  const searchQueries = useQueries({
    queries: searchFolders.map((f) => ({
      queryKey: ['wiki', 'disk', 'files', f.id],
      queryFn: () => diskApi.files(f.id),
      enabled: searching,
      retry: false,
      staleTime: 30_000,
    })),
  });
  const searchLoading = searching && searchQueries.some((q) => q.isLoading);
  // Файлов на Диске сотни — фильтр по имени на каждый рендер дешёвый, мемо не нужно.
  const hits = searching
    ? searchDiskFiles(
        query,
        searchFolders.map((f, i) => ({ folderId: f.id, files: searchQueries[i]?.data ?? [] })),
        folders,
      )
    : [];

  const toggle = (file: DiskFileDto): void =>
    setChecked((prev) => {
      const next = new Map(prev);
      if (next.has(file.id)) next.delete(file.id);
      else next.set(file.id, { id: file.id, fileName: file.fileName, size: file.sizeBytes });
      return next;
    });

  const crumbs = folderPath(folders, folderId);
  const subFolders = folders.filter((f) => (f.parentId ?? null) === folderId);
  const files = filesQuery.data ?? [];

  const fileRow = (file: DiskFileDto, path?: string): JSX.Element => (
    <div
      key={file.id}
      role="checkbox"
      aria-checked={checked.has(file.id)}
      tabIndex={0}
      onClick={() => toggle(file)}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          toggle(file);
        }
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '9px 12px',
        borderRadius: 'var(--fk-radius-md)',
        border: `1px solid ${checked.has(file.id) ? 'var(--fk-orange)' : 'var(--fk-border)'}`,
        background: checked.has(file.id) ? 'var(--fk-orange-soft)' : 'var(--fk-surface)',
        cursor: 'pointer',
      }}
    >
      {/* Клик ловит вся строка; сам чекбокс — только индикатор. */}
      <Checkbox checked={checked.has(file.id)} style={{ pointerEvents: 'none' }} tabIndex={-1} />
      <span style={{ fontSize: 20, flexShrink: 0 }}>{fileIcon(file.fileName)}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {file.fileName}
        </span>
        {path && (
          <span style={{ display: 'block', fontSize: 11, color: 'var(--fk-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {path}
          </span>
        )}
      </span>
      <span style={{ fontSize: 12, color: 'var(--fk-text-muted)', flexShrink: 0 }}>{formatSize(file.sizeBytes)}</span>
    </div>
  );

  let list: JSX.Element;
  if (foldersQuery.isLoading) {
    list = <Skeleton active paragraph={{ rows: 5 }} />;
  } else if (foldersQuery.isError) {
    list = <Alert type="error" showIcon message="Не удалось открыть Диск" description={(foldersQuery.error as Error).message} />;
  } else if (folders.length === 0) {
    list = (
      <Empty description={<span>На Диске пока нет файлов. Загрузить можно на странице <Link to="/disk" target="_blank">«Диск»</Link>.</span>} />
    );
  } else if (searching) {
    list = searchLoading && hits.length === 0 ? (
      <Skeleton active paragraph={{ rows: 4 }} />
    ) : hits.length === 0 ? (
      <Empty description={`Ничего не нашли по «${query.trim()}»`} />
    ) : (
      <>{hits.map((h) => fileRow(h.file, h.path))}</>
    );
  } else {
    list = (
      <>
        {subFolders.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFolderId(f.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 12px',
              borderRadius: 'var(--fk-radius-md)',
              border: '1px solid var(--fk-border)',
              background: 'var(--fk-surface)',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 14,
              fontWeight: 600,
              textAlign: 'left',
            }}
          >
            <span style={{ color: f.color, display: 'inline-flex', fontSize: 18 }}><FolderOutlined /></span>
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
            <span style={{ fontSize: 12, color: 'var(--fk-text-muted)', fontWeight: 400 }}>{totals.get(f.id) ?? f.fileCount}</span>
            <RightOutlined style={{ fontSize: 12, color: 'var(--fk-text-muted)' }} />
          </button>
        ))}
        {folderId && filesQuery.isLoading && <Skeleton active paragraph={{ rows: 3 }} />}
        {folderId && filesQuery.isError && (
          <Alert type="error" showIcon message="Не удалось загрузить файлы папки" description={(filesQuery.error as Error).message} />
        )}
        {folderId && !filesQuery.isLoading && !filesQuery.isError && files.map((f) => fileRow(f))}
        {folderId && !filesQuery.isLoading && !filesQuery.isError && files.length === 0 && subFolders.length === 0 && (
          <Empty description="В этой папке пока нет файлов" />
        )}
        {!folderId && subFolders.length === 0 && <Empty description="Папок пока нет" />}
      </>
    );
  }

  const picked = [...checked.values()];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Input
        autoFocus
        allowClear
        prefix={<SearchOutlined />}
        placeholder="Найти файл по названию (от 2 букв)"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {!searching && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, fontSize: 13, color: 'var(--fk-text-2)' }}>
          <Button type="link" size="small" style={{ padding: 0 }} disabled={!folderId} onClick={() => setFolderId(null)}>
            Диск
          </Button>
          {crumbs.map((c, i) => (
            <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <RightOutlined style={{ fontSize: 10 }} />
              {i === crumbs.length - 1 ? (
                <b>{c.name}</b>
              ) : (
                <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setFolderId(c.id)}>
                  {c.name}
                </Button>
              )}
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minHeight: 200, maxHeight: 360, overflowY: 'auto' }}>{list}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
        <span style={{ fontSize: 13, color: 'var(--fk-text-muted)' }}>
          {picked.length === 0 ? 'Отметьте один или несколько файлов' : `Выбрано: ${picked.length}`}
        </span>
        <span style={{ display: 'inline-flex', gap: 8 }}>
          <Button onClick={onCancel}>Отмена</Button>
          <Button type="primary" disabled={picked.length === 0} onClick={() => onPick(picked)}>
            {picked.length > 1 ? `Вставить файлы (${picked.length})` : 'Вставить файл'}
          </Button>
        </span>
      </div>
    </div>
  );
}
